import moment from "moment";

/** Partner TC “stale pending” threshold — keep in sync across admin TC screens. */
export const TC_ADMIN_PENDING_STALE_DAYS = 7;

/** Aligns with backend resolveClinicListingDecision / isClinicPlatformListingApproved */
export function isClinicListedForTeleconsultationPlatform(clinic) {
  if (!clinic) return false;
  const decision = String(clinic.platformListingDecision || "").toLowerCase();
  if (decision === "approved") return true;
  if (decision === "rejected" || decision === "pending") return false;
  // Legacy fallback until all clinics have platformListingDecision
  const s = clinic.tcPlatformReviewStatus;
  if (s === "approved") return true;
  if (s === "rejected" || s === "pending_review") return false;
  return clinic.teleconsultationPlatformApproved !== false;
}

export function buildClinicMapByPartner(tcClinics) {
  const map = new Map();
  (Array.isArray(tcClinics) ? tcClinics : []).forEach((c) => {
    const pid = c?.partner?._id || c?.partner;
    if (!pid) return;
    const key = String(pid);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  });
  return map;
}

export function derivePartnerTcStatus(partner, clinicMapByPartner) {
  const explicit = String(partner?.tcApprovalStatus || "").toLowerCase();
  if (explicit === "not_requested") return "off";
  if (["off", "pending", "approved", "rejected"].includes(explicit)) return explicit;

  const clinics = clinicMapByPartner.get(String(partner?._id || "")) || [];
  const hasActive = clinics.some((c) => String(c?.status || "").toLowerCase() === "active");
  if (hasActive) return "approved";

  const hasPending = clinics.some((c) => {
    const s = String(c?.status || "").toLowerCase();
    return s === "pending" || s === "draft";
  });
  return hasPending ? "pending" : "off";
}

export function selectPartnersPendingTooLong(
  pendingPartnerRequests,
  staleDays = TC_ADMIN_PENDING_STALE_DAYS
) {
  const cutoff = moment().subtract(staleDays, "days");
  return pendingPartnerRequests.filter((p) => {
    const d = p.tcApprovalUpdatedAt || p.createdAt;
    if (!d) return true;
    return moment(d).isBefore(cutoff);
  });
}

export function selectPaidSoonWithoutPractitioner(consultations) {
  const now = moment();
  const horizon = moment().add(24, "hours");
  return (Array.isArray(consultations) ? consultations : []).filter((c) => {
    if (String(c.status || "").toLowerCase() !== "paid") return false;
    if (c.practitioner) return false;
    if (!c.scheduledAt) return false;
    const t = moment(c.scheduledAt);
    return t.isSameOrAfter(now) && t.isSameOrBefore(horizon);
  });
}

export function platformApprovedActiveTcClinicsCount(tcClinics) {
  return (Array.isArray(tcClinics) ? tcClinics : []).filter(
    (c) =>
      isClinicListedForTeleconsultationPlatform(c) && String(c?.status || "").toLowerCase() === "active"
  ).length;
}

/** Human label for admin list/detail (scheduled = unpaid checkout). */
export function getTeleconsultationStatusDisplayLabel(status) {
  const s = String(status || "")
    .toLowerCase()
    .replace(/-/g, "_");
  if (s === "scheduled") return "Payment pending";
  return status ? String(status) : "—";
}

/** Badge tone for `badge-${tone}` / `badge-dim` (align List + Detail). */
export function getTeleconsultationStatusBadgeTone(status) {
  const s = String(status || "")
    .toLowerCase()
    .replace(/-/g, "_");
  if (s === "completed" || s === "paid") return "success";
  if (s === "scheduled" || s === "pending") return "warning";
  if (s === "cancelled" || s === "canceled") return "danger";
  if (s === "no_show" || s === "no_show_patient" || s === "no_show_provider") return "info";
  if (s === "technical_issue") return "warning";
  return "secondary";
}
